/**
 * Admin → Students → row actions → "Edit student" dialog.
 *
 * Three tabs:
 *   1. Profile     — name / email / phone / country / residency / role
 *                    (writes to PATCH /admin/users/{id})
 *   2. Enrollment  — start / end dates, status, residency, tuition, LOA
 *                    status, internal notes (writes to PATCH
 *                    /admin/enrollments/{id})
 *   3. History     — audit-log entries scoped to the enrollment, newest
 *                    first, with a per-row diff showing exactly which
 *                    fields changed.
 *
 * The dialog only edits ONE enrollment at a time. For students with
 * multiple enrollments we render a small picker at the top of the
 * Enrollment & History tabs so the registrar can target each separately.
 */
import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
    User, BookOpen, History as HistoryIcon, Loader2, Save, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = ["active", "completed", "suspended", "withdrawn"];
const RESIDENCY_OPTIONS = ["domestic", "international"];
const LOA_STATUS_OPTIONS = ["pending", "issued", "not_required"];
const ROLE_OPTIONS = ["student", "instructor", "sme", "admin"];

function fmt(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

/**
 * Render one audit-log entry: "Super Admin · changed start_date · 5 Jun".
 * The diff is shown as a tight two-column list under the headline.
 */
function HistoryRow({ entry }) {
    const changes = entry?.extra?.changes || {};
    const keys = Object.keys(changes);
    return (
        <div
            className="border border-border rounded-sm p-3 bg-card"
            data-testid={`history-entry-${entry.id}`}
        >
            <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-sm">
                    <strong>{entry.actor_name || entry.actor_email}</strong>
                    <span className="text-muted-foreground">
                        {" "}· {entry.actor_role} · {entry.action.replace(/_/g, " ")}
                    </span>
                </div>
                <span className="text-[11px] text-muted-foreground tabular-nums">
                    {fmt(entry.at)}
                </span>
            </div>
            {keys.length > 0 ? (
                <div className="space-y-1">
                    {keys.map((k) => (
                        <div key={k} className="grid grid-cols-[140px_1fr] gap-2 text-[12px]">
                            <span className="font-mono text-muted-foreground">{k}</span>
                            <span>
                                <span className="line-through text-destructive/70">
                                    {String(changes[k].old ?? "—")}
                                </span>
                                {" → "}
                                <span className="text-emerald-700 font-medium">
                                    {String(changes[k].new ?? "—")}
                                </span>
                            </span>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-xs text-muted-foreground italic">No field diff recorded.</p>
            )}
        </div>
    );
}

export default function EditStudentDialog({ open, onOpenChange, student, onSaved }) {
    const enrollments = useMemo(
        () => student?.enrollments || [],
        [student],
    );
    const [activeEnrId, setActiveEnrId] = useState(
        enrollments[0]?.id || enrollments[0]?.enrollment_id || null,
    );
    const [profile, setProfile] = useState({});
    const [enrollment, setEnrollment] = useState({});
    const [history, setHistory] = useState([]);
    const [savingProfile, setSavingProfile] = useState(false);
    const [savingEnr, setSavingEnr] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Render-time prop-sync (React-recommended pattern, avoids the
    // `set-state-in-effect` lint and a stale-state frame). When the
    // dialog is opened on a different student we hydrate the profile
    // form fields synchronously from the new prop.
    const [lastStudentKey, setLastStudentKey] = useState(null);
    const currentKey = open ? (student?.id || null) : null;
    if (currentKey && lastStudentKey !== currentKey) {
        setLastStudentKey(currentKey);
        setProfile({
            name: student.name || "",
            email: student.email || "",
            phone: student.phone || "",
            country: student.country || "",
            residency: student.residency || "domestic",
            role: student.role || "student",
        });
        setActiveEnrId(enrollments[0]?.id || enrollments[0]?.enrollment_id || null);
    } else if (!currentKey && lastStudentKey !== null) {
        // Dialog closed — clear the cached key so the next open re-hydrates.
        setLastStudentKey(null);
    }

    const activeEnrollment = useMemo(
        () => enrollments.find((e) => (e.id || e.enrollment_id) === activeEnrId),
        [enrollments, activeEnrId],
    );

    // Same pattern for the enrollment form: re-hydrate when the user
    // picks a different enrollment from the picker.
    const [lastEnrKey, setLastEnrKey] = useState(null);
    const enrKey = activeEnrollment ? (activeEnrollment.id || activeEnrollment.enrollment_id) : null;
    if (enrKey && lastEnrKey !== enrKey) {
        setLastEnrKey(enrKey);
        setEnrollment({
            start_date: activeEnrollment.start_date || "",
            end_date: activeEnrollment.end_date || "",
            status: activeEnrollment.status || "active",
            residency: activeEnrollment.residency || "domestic",
            tuition_amount: activeEnrollment.tuition_amount ?? "",
            loa_status: activeEnrollment.loa_status || "pending",
            internal_notes: activeEnrollment.internal_notes || "",
        });
    }

    // Load history when the user opens the tab OR when activeEnrId changes.
    const loadHistory = async () => {
        if (!activeEnrId) return;
        setLoadingHistory(true);
        try {
            const { data } = await api.get(
                `/admin/enrollments/${activeEnrId}/history`,
            );
            setHistory(data.items || []);
        } catch (e) {
            toast.error("Could not load history.");
        } finally {
            setLoadingHistory(false);
        }
    };

    const saveProfile = async () => {
        if (!student?.id) return;
        setSavingProfile(true);
        try {
            await api.patch(`/admin/users/${student.id}`, profile);
            toast.success("Profile saved.");
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save profile.");
        } finally {
            setSavingProfile(false);
        }
    };

    const saveEnrollment = async () => {
        if (!activeEnrId) return;
        setSavingEnr(true);
        try {
            // Strip empty strings → null so we don't blank a field unintentionally.
            const payload = {};
            for (const [k, v] of Object.entries(enrollment)) {
                if (v === "") continue;
                payload[k] = k === "tuition_amount" ? Number(v) : v;
            }
            const { data } = await api.patch(
                `/admin/enrollments/${activeEnrId}`, payload,
            );
            const changedCount = Object.keys(data?.changes || {}).length;
            toast.success(
                changedCount
                    ? `${changedCount} field${changedCount === 1 ? "" : "s"} updated.`
                    : "No changes to save.",
            );
            onSaved?.();
            // Refresh history immediately so the user sees the new entry.
            loadHistory();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save enrollment.");
        } finally {
            setSavingEnr(false);
        }
    };

    if (!student) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl w-[min(900px,calc(100vw-2rem))] max-h-[90vh] flex flex-col p-0 gap-0"
                data-testid="edit-student-dialog"
            >
                <DialogHeader className="px-6 pt-5 pb-3 border-b border-border space-y-1">
                    <DialogTitle className="flex items-center gap-2 font-display">
                        Edit student — {student.name || student.email}
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        Every save writes an audit log entry. Use the History tab to
                        see who changed what on this enrollment.
                    </DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="profile" className="flex-1 flex flex-col min-h-0">
                    <TabsList className="mx-6 mt-3 self-start">
                        <TabsTrigger value="profile" data-testid="edit-tab-profile">
                            <User className="h-3.5 w-3.5 mr-1.5" /> Profile
                        </TabsTrigger>
                        <TabsTrigger
                            value="enrollment"
                            data-testid="edit-tab-enrollment"
                            disabled={!enrollments.length}
                        >
                            <BookOpen className="h-3.5 w-3.5 mr-1.5" /> Enrollment
                        </TabsTrigger>
                        <TabsTrigger
                            value="history"
                            onClick={loadHistory}
                            data-testid="edit-tab-history"
                            disabled={!enrollments.length}
                        >
                            <HistoryIcon className="h-3.5 w-3.5 mr-1.5" /> History
                        </TabsTrigger>
                    </TabsList>

                    {/* ---------- PROFILE ---------- */}
                    <TabsContent
                        value="profile"
                        className="flex-1 overflow-y-auto px-6 py-4 space-y-3 m-0"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Full name</Label>
                                <Input
                                    value={profile.name || ""}
                                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                    data-testid="profile-name"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Email</Label>
                                <Input
                                    type="email"
                                    value={profile.email || ""}
                                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                                    data-testid="profile-email"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Phone</Label>
                                <Input
                                    value={profile.phone || ""}
                                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                                    data-testid="profile-phone"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Country</Label>
                                <Input
                                    value={profile.country || ""}
                                    onChange={(e) => setProfile({ ...profile, country: e.target.value })}
                                    data-testid="profile-country"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Residency</Label>
                                <Select
                                    value={profile.residency || "domestic"}
                                    onValueChange={(v) => setProfile({ ...profile, residency: v })}
                                >
                                    <SelectTrigger data-testid="profile-residency"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {RESIDENCY_OPTIONS.map((r) => (
                                            <SelectItem key={r} value={r}>{r}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs flex items-center gap-1">
                                    Role
                                    {student.role === "super_admin" && (
                                        <ShieldAlert className="h-3 w-3 text-amber-600" />
                                    )}
                                </Label>
                                <Select
                                    value={profile.role || "student"}
                                    onValueChange={(v) => setProfile({ ...profile, role: v })}
                                    disabled={student.role === "super_admin"}
                                >
                                    <SelectTrigger data-testid="profile-role"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {ROLE_OPTIONS.map((r) => (
                                            <SelectItem key={r} value={r}>{r}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {student.role === "super_admin" && (
                                    <p className="text-[11px] text-amber-700 mt-1">
                                        Super admin role can&apos;t be changed from this dialog.
                                    </p>
                                )}
                            </div>
                        </div>
                        <DialogFooter className="pt-3">
                            <Button
                                onClick={saveProfile}
                                disabled={savingProfile}
                                data-testid="save-profile-btn"
                                className="gap-1.5"
                            >
                                {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                Save profile
                            </Button>
                        </DialogFooter>
                    </TabsContent>

                    {/* ---------- ENROLLMENT ---------- */}
                    <TabsContent
                        value="enrollment"
                        className="flex-1 overflow-y-auto px-6 py-4 space-y-3 m-0"
                    >
                        {enrollments.length > 1 && (
                            <div>
                                <Label className="text-xs">Pick enrollment</Label>
                                <Select
                                    value={activeEnrId || ""}
                                    onValueChange={setActiveEnrId}
                                >
                                    <SelectTrigger data-testid="enr-picker"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {enrollments.map((e) => (
                                            <SelectItem
                                                key={e.id || e.enrollment_id}
                                                value={e.id || e.enrollment_id}
                                            >
                                                {e.program_title || e.program_id}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Intake start date</Label>
                                <Input
                                    type="date"
                                    value={enrollment.start_date || ""}
                                    onChange={(e) => setEnrollment({ ...enrollment, start_date: e.target.value })}
                                    data-testid="enr-start-date"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Intake end date</Label>
                                <Input
                                    type="date"
                                    value={enrollment.end_date || ""}
                                    onChange={(e) => setEnrollment({ ...enrollment, end_date: e.target.value })}
                                    data-testid="enr-end-date"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Status</Label>
                                <Select
                                    value={enrollment.status || "active"}
                                    onValueChange={(v) => setEnrollment({ ...enrollment, status: v })}
                                >
                                    <SelectTrigger data-testid="enr-status"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {STATUS_OPTIONS.map((s) => (
                                            <SelectItem key={s} value={s}>{s}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs">Residency</Label>
                                <Select
                                    value={enrollment.residency || "domestic"}
                                    onValueChange={(v) => setEnrollment({ ...enrollment, residency: v })}
                                >
                                    <SelectTrigger data-testid="enr-residency"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {RESIDENCY_OPTIONS.map((r) => (
                                            <SelectItem key={r} value={r}>{r}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs">Tuition paid (CAD)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={enrollment.tuition_amount ?? ""}
                                    onChange={(e) => setEnrollment({ ...enrollment, tuition_amount: e.target.value })}
                                    data-testid="enr-tuition"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">LOA status</Label>
                                <Select
                                    value={enrollment.loa_status || "pending"}
                                    onValueChange={(v) => setEnrollment({ ...enrollment, loa_status: v })}
                                >
                                    <SelectTrigger data-testid="enr-loa-status"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {LOA_STATUS_OPTIONS.map((s) => (
                                            <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Internal notes (registrar-only)</Label>
                            <Textarea
                                rows={3}
                                value={enrollment.internal_notes || ""}
                                onChange={(e) => setEnrollment({ ...enrollment, internal_notes: e.target.value })}
                                placeholder="e.g. 'Transferred from cohort A — verified by Jane on 5/12.'"
                                data-testid="enr-notes"
                            />
                        </div>
                        <DialogFooter className="pt-3">
                            <Button
                                onClick={saveEnrollment}
                                disabled={savingEnr || !activeEnrId}
                                data-testid="save-enrollment-btn"
                                className="gap-1.5"
                            >
                                {savingEnr ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                Save enrollment
                            </Button>
                        </DialogFooter>
                    </TabsContent>

                    {/* ---------- HISTORY ---------- */}
                    <TabsContent
                        value="history"
                        className="flex-1 overflow-y-auto px-6 py-4 m-0"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <Badge variant="outline" className="text-[11px]">
                                {history.length} change{history.length === 1 ? "" : "s"} recorded
                            </Badge>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={loadHistory}
                                disabled={loadingHistory}
                                data-testid="refresh-history-btn"
                            >
                                {loadingHistory ? "Loading…" : "Refresh"}
                            </Button>
                        </div>
                        {loadingHistory ? (
                            <div className="text-center text-sm text-muted-foreground py-10">
                                Loading history…
                            </div>
                        ) : history.length === 0 ? (
                            <div className="text-center text-sm text-muted-foreground py-10">
                                No edits recorded yet. Saved changes from this dialog will show here.
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {history.map((h) => (
                                    <HistoryRow key={h.id} entry={h} />
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}
